import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatDate, formatEventWhen, formatINR } from "@/lib/sections";
import { toast, Toaster } from "sonner";
import { ChevronLeft, ChevronRight, MapPin, Clock, User, Tag, Calendar, X, Download } from "lucide-react";
import ExhibitionVisitPanel from "@/components/ExhibitionVisitPanel";

const WEEKDAY_NAMES = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

// Return the next N dates whose weekday (0=Mon … 6=Sun) is in the allowed set.
// Chronological, includes today if it matches. Uses LOCAL calendar dates (IST for the user).
const nextMatchingDates = (weekdays, n = 8) => {
  const out = [];
  const allowed = new Set((weekdays || []).map(Number).filter(v => v >= 0 && v <= 6));
  if (!allowed.size) return out;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cursor = new Date(today);
  // Cap iterations so we never loop forever
  let safety = 0;
  while (out.length < n && safety < 365 * 3) {
    // JS weekday: 0=Sun, 1=Mon … 6=Sat. Ours: 0=Mon … 6=Sun.
    const ourWeekday = (cursor.getDay() + 6) % 7;
    if (allowed.has(ourWeekday)) {
      out.push(new Date(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
    safety++;
  }
  return out;
};

const weekdayLabelList = (weekdays) => (weekdays || [])
  .map(v => WEEKDAY_NAMES[v])
  .filter(Boolean);

const humanWeekdays = (weekdays) => {
  const names = weekdayLabelList(weekdays);
  if (names.length === 0) return "session";
  if (names.length === 1) return names[0];
  if (names.length === 2) return names.join(" or ");
  return names.slice(0, -1).join(", ") + " or " + names.at(-1);
};

const isoLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatSessionDate = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: 1, notes: "", session_date: "" });
  // Keep the qty field as a string so users can clear it and type a new number on mobile
  // without it snapping back to 1 on every keystroke.
  const [qtyInput, setQtyInput] = useState("1");
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/events/${slug}`)
      .then((r) => setEvent(r.data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const mode = event?.booking_mode || "one_off";
  const isWalkInExhibition = event?.category === "exhibition" && !(event?.price_inr > 0);
  // Normalise gallery images: legacy events store plain URL strings, newer ones {url, caption}
  const galleryImages = useMemo(
    () => (event?.gallery_images || []).map(g => (typeof g === "string" ? { url: g, caption: "" } : g)),
    [event]
  );
  const weekdays = useMemo(() => {
    if (!event) return [];
    const list = Array.isArray(event.recurrence_weekdays) && event.recurrence_weekdays.length
      ? event.recurrence_weekdays
      : (event.recurrence_weekday != null ? [event.recurrence_weekday] : []);
    return list.map(Number);
  }, [event]);

  // Show up to 12 upcoming dates for per_session so multi-day events (e.g. co-working Wed-Sat) look full
  const sessionOptions = useMemo(() => nextMatchingDates(weekdays, 12), [weekdays]);
  const courseDates = useMemo(() => {
    if (mode !== "full_course") return [];
    const n = Math.max(1, Number(event?.session_count || 1));
    return nextMatchingDates(weekdays, n);
  }, [event, mode, weekdays]);

  // Auto-pick the first option for per_session so the price displays right away
  useEffect(() => {
    if (mode === "per_session" && !form.session_date && sessionOptions.length) {
      setForm(f => ({ ...f, session_date: isoLocalDate(sessionOptions[0]) }));
    }
  }, [mode, sessionOptions, form.session_date]);

  // Lightbox for the extra gallery images (thumbnail strip below the description)
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Live availability preview — hit /events/{slug}/availability when the buyer changes date
  useEffect(() => {
    if (!event) return;
    // Only fetch if we know which date to check
    const dateToCheck = mode === "per_session"
      ? form.session_date
      : mode === "full_course" && courseDates[0] ? isoLocalDate(courseDates[0])
      : (event.starts_at || "").slice(0, 10);
    if (!dateToCheck) { setAvailability(null); return; }
    let cancelled = false;
    api.get(`/events/${event.slug}/availability`, { params: { session_date: dateToCheck } })
      .then(r => { if (!cancelled) setAvailability(r.data); })
      .catch(() => { if (!cancelled) setAvailability(null); });
    return () => { cancelled = true; };
  }, [event, mode, form.session_date, courseDates]);

  // Keyboard navigation for the lightbox (Esc closes, arrows step through)
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      const count = galleryImages.length;
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowRight") setLightboxIdx(i => Math.min(count - 1, i + 1));
      if (e.key === "ArrowLeft") setLightboxIdx(i => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, galleryImages]);

  // Course fee is a flat total — never multiply by session count on the frontend either.
  const total = event ? Number(event.price_inr || 0) * Number(form.quantity || 1) : 0;

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!event) return;
    setSubmitting(true);
    try {
      const payload = {
        event_id: event.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        quantity: Number(form.quantity),
        notes: form.notes,
        // Only send session_date for per_session bookings; full_course batches are computed server-side
        session_date: mode === "per_session" ? form.session_date : null,
      };
      const { data } = await api.post("/bookings", payload);

      // Free event — skip Razorpay, mark as paid via verify with empty signature in mock mode
      if (data.amount_inr === 0 || data.is_mock) {
        await api.post("/bookings/verify", {
          booking_id: data.booking_id,
          razorpay_order_id: data.razorpay_order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: "mock_signature",
        });
        toast.success("Booking confirmed.");
        navigate(`/confirmation/${data.booking_id}`);
        return;
      }

      // Real Razorpay flow
      if (!window.Razorpay) {
        toast.error("Razorpay SDK not loaded");
        return;
      }
      const options = {
        key: data.razorpay_key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: "Carpe Diem Goa",
        description: event.title,
        order_id: data.razorpay_order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#C05C42" },
        handler: async (response) => {
          try {
            await api.post("/bookings/verify", {
              booking_id: data.booking_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment confirmed.");
            navigate(`/confirmation/${data.booking_id}`);
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.message("Payment cancelled") },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-24 text-muted2">Loading…</div>;
  if (!event) return (
    <div className="p-24 text-center">
      <div className="font-serif text-4xl">Event not found.</div>
      <Link to="/events" className="mt-6 inline-block link-hover" data-testid="event-not-found-back">Back to all events</Link>
    </div>
  );

  return (
    <div data-testid="event-detail-page" className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12 lg:py-16">
      <Toaster position="top-center" richColors />
      <Link to="/events" className="inline-flex items-center gap-1 text-sm link-hover" data-testid="event-back-link">
        <ChevronLeft size={16} /> Back to events
      </Link>

      <div className="mt-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] overflow-hidden bg-cream-2 border border-soft">
            <img src={event.image_url} alt={event.title} className="w-full h-full object-contain" />
          </div>
        </div>

        <div className="lg:col-span-5 lg:pt-4">
          <div className="overline">{event.section?.replace('-', ' ')} · {event.category}</div>
          <h1 className="display text-4xl md:text-5xl lg:text-6xl mt-4">{event.title}</h1>
          <p className="mt-6 text-base text-muted2 leading-relaxed">{event.short_description}</p>

          <dl className="mt-8 space-y-3 text-sm">
            {event.instructor && (
              <div className="flex items-center gap-2"><User size={14} className="text-sage"/> <span className="text-muted2">{isWalkInExhibition ? "By" : "Led by"}</span> <span>{event.instructor}</span></div>
            )}
            <div className="flex items-center gap-2"><Clock size={14} className="text-sage"/> <span>{formatEventWhen(event)}</span></div>
            <div className="flex items-center gap-2"><MapPin size={14} className="text-sage"/> <span>{event.location}</span></div>
            <div className="flex items-center gap-2"><Tag size={14} className="text-sage"/> <span>{event.price_inr > 0 ? formatINR(event.price_inr) + " per seat" : "Free admission"}</span></div>
          </dl>

          <div className="hairline my-8" />

          {isWalkInExhibition ? <ExhibitionVisitPanel /> : (
          <form onSubmit={handleBooking} className="space-y-4" data-testid="booking-form">
            <div className="overline">Reserve your seat</div>

            {/* Session picker for recurring per-session events */}
            {mode === "per_session" && sessionOptions.length > 0 && (
              <div>
                <label className="text-xs text-muted2 flex items-center gap-1.5 mb-2">
                  <Calendar size={12} /> Choose your date · {humanWeekdays(weekdays)}
                </label>
                <select
                  required
                  value={form.session_date}
                  onChange={(e) => setForm({ ...form, session_date: e.target.value })}
                  className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
                  data-testid="booking-session-date"
                >
                  {sessionOptions.map((d) => (
                    <option key={isoLocalDate(d)} value={isoLocalDate(d)}>{formatSessionDate(d)}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Live availability preview — appears only when the event has capacity data */}
            {availability && availability.capacity > 0 && (
              <div
                className={`text-xs px-3 py-2 border ${availability.remaining <= 0 ? "border-terra text-terra" : availability.remaining <= 2 ? "border-terra text-terra" : "border-soft text-muted2"}`}
                data-testid="booking-availability"
                style={availability.remaining <= 2 ? { color: "#C05C42", borderColor: "#C05C42" } : {}}
              >
                {availability.remaining <= 0
                  ? "Fully booked for this date — please pick another."
                  : availability.remaining === 1
                    ? "Only 1 spot left for this date."
                    : `${availability.remaining} of ${availability.capacity} spots left${availability.pool ? " (shared across all wheel-throwing classes)" : ""}.`}
              </div>
            )}

            {/* Fixed batch preview for full-course booking */}
            {mode === "full_course" && courseDates.length > 0 && (
              <div className="bg-cream-2 border border-soft p-4 text-xs" data-testid="booking-course-batch">
                <div className="overline mb-2 flex items-center gap-1.5"><Calendar size={12}/> Course batch — {courseDates.length} sessions</div>
                <ul className="space-y-1">
                  {courseDates.map((d, i) => (
                    <li key={i} className="text-charcoal/80"><span className="text-muted2 mr-2">{i + 1}.</span>{formatSessionDate(d)}</li>
                  ))}
                </ul>
                <div className="text-[11px] text-muted2 mt-2">The fee below is a flat total for the whole course of {courseDates.length} sessions.</div>
              </div>
            )}

            <input
              required
              type="text"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
              data-testid="booking-name"
            />
            <input
              required
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
              data-testid="booking-email"
            />
            <input
              required
              type="tel"
              placeholder="Phone (+91…)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
              data-testid="booking-phone"
            />
            <div className="flex items-center gap-3">
              <label className="text-sm text-muted2">Seats</label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={10}
                value={qtyInput}
                onChange={(e) => {
                  const raw = e.target.value;
                  setQtyInput(raw);
                  // Only update the numeric form value when it's a real number ≥1;
                  // an empty field or "0" is treated as "no value yet" so users can retype.
                  const n = parseInt(raw, 10);
                  if (Number.isFinite(n) && n >= 1) {
                    setForm({ ...form, quantity: Math.min(10, n) });
                  }
                }}
                onBlur={() => {
                  // On blur, snap the visible value to the clamped quantity (1–10).
                  const n = parseInt(qtyInput, 10);
                  const safe = Number.isFinite(n) && n >= 1 ? Math.min(10, n) : 1;
                  setQtyInput(String(safe));
                  setForm(f => ({ ...f, quantity: safe }));
                }}
                className="w-20 bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
                data-testid="booking-quantity"
              />
              <div className="ml-auto font-serif text-2xl">{total > 0 ? formatINR(total) : "Free"}</div>
            </div>
            <textarea
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full bg-transparent border-b border-soft py-3 text-sm focus:outline-none focus:border-charcoal"
              data-testid="booking-notes"
            />
            <button
              disabled={submitting || (availability && availability.capacity > 0 && availability.remaining <= 0)}
              type="submit"
              className="btn-primary w-full justify-center mt-4 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="booking-submit"
            >
              {submitting ? "Processing…"
                : (availability && availability.capacity > 0 && availability.remaining <= 0) ? "Sold out for this date"
                : (event.price_inr > 0 ? "Pay & confirm" : "Reserve seat")}
            </button>
            <p className="text-xs text-muted2 leading-relaxed">
              Payments are secured via Razorpay. A confirmation email with your booking ID will be sent to the email above.
            </p>
          </form>
          )}
        </div>
      </div>

      {/* Description + catalogue download */}
      {(event.description || event.catalog_url) && (
        <div className="mt-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <div className="overline">About this</div>
            <div className="font-serif text-3xl mt-3">Details.</div>
          </div>
          <div className="lg:col-span-7">
            {event.description && <p className="text-base leading-relaxed whitespace-pre-wrap">{event.description}</p>}
            {event.catalog_url && (
              <a
                href={`${event.catalog_url}?download=1`}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost inline-flex items-center gap-2 mt-8 text-sm"
                data-testid="event-catalog-download"
              >
                <Download size={14} /> Download catalogue{event.catalog_filename ? ` · ${event.catalog_filename.split(".").pop().toUpperCase()}` : ""}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Additional imagery — thumbnail strip that opens a lightbox */}
      {galleryImages.length > 0 && (
        <div className="mt-16" data-testid="event-gallery-images">
          <div className="overline mb-4">Gallery</div>
          <div className="flex flex-wrap items-start gap-4">
            {galleryImages.map((img, i) => (
              <figure key={i} className="m-0">
                <button
                  type="button"
                  onClick={() => setLightboxIdx(i)}
                  className="overflow-hidden border border-soft bg-cream-2 focus:outline-none focus:ring-1 focus:ring-charcoal"
                  data-testid={`event-gallery-thumb-${i}`}
                >
                  <img src={img.url} alt={img.caption || `${event.title} · view ${i + 1}`} loading="lazy" className="h-28 md:h-40 w-auto max-w-[75vw] md:max-w-[420px] object-contain block transition-transform duration-300 hover:scale-[1.03]" />
                </button>
                {img.caption && (
                  <figcaption className="mt-1.5 text-[11px] text-muted2 leading-snug max-w-[220px]" data-testid={`event-gallery-caption-${i}`}>
                    {img.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox overlay */}
      {lightboxIdx !== null && galleryImages[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 md:p-12"
          onClick={() => setLightboxIdx(null)}
          data-testid="event-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            onClick={() => setLightboxIdx(null)}
            data-testid="event-lightbox-close"
            aria-label="Close"
          ><X size={28}/></button>
          {lightboxIdx > 0 && (
            <button
              className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => i - 1); }}
              data-testid="event-lightbox-prev"
              aria-label="Previous"
            ><ChevronLeft size={36}/></button>
          )}
          <img
            src={galleryImages[lightboxIdx].url}
            alt={galleryImages[lightboxIdx].caption || `${event.title} · view ${lightboxIdx + 1}`}
            className="max-w-full max-h-[85vh] w-auto h-auto object-contain"
            onClick={(e) => e.stopPropagation()}
            data-testid="event-lightbox-image"
          />
          {lightboxIdx < galleryImages.length - 1 && (
            <button
              className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => i + 1); }}
              data-testid="event-lightbox-next"
              aria-label="Next"
            ><ChevronRight size={36}/></button>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center max-w-[80vw]">
            {galleryImages[lightboxIdx].caption && (
              <div className="text-white/85 text-sm mb-1" data-testid="event-lightbox-caption">{galleryImages[lightboxIdx].caption}</div>
            )}
            <div className="text-white/60 text-xs tracking-widest" data-testid="event-lightbox-counter">
              {lightboxIdx + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
